import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Zap, Target, CheckCircle2, Star, Users, Gift, Sparkles } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView, AnimatePresence } from 'framer-motion';
import { UrgencyBanner } from './components/UrgencyBanner';
import { BonusCard } from './components/BonusCard';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { WhatYouReceive } from './components/WhatYouReceive';
import { EnergyBackground } from './components/EnergyBackground';
import { LavenderBranches } from './components/LavenderBranches';
import { HeroAmbientEffects } from './components/HeroAmbientEffects';
import { GiftBackground } from './components/GiftBackground';
import { LifetimeAccessBonus } from './components/LifetimeAccessBonus';
import { FloatingEnergySymbols } from './components/FloatingEnergySymbols';
import { PremiumBonusEffect, TitleSparkles } from './components/PremiumBonusEffect';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toLocaleString('es-AR');
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(count, to, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    }
  }, [count, to, isInView]);
  return <motion.span ref={ref} data-id="element-0" data-mp="38m1jadapbvsn">{rounded}</motion.span>;
}
const testimonials = [{
  image: 'https://cdn.magicpatterns.com/uploads/4E8eYDWnfkpYBG2afdcu5M/454ebf8f-4321-4538-84ea-d616e90230e4.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/wxNtKWsuN2RmMwYU2JgRRc/18f026a7-f17f-4618-ae8e-46ff64ed4b11.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/dmXs9mokmshvmFFWeT7Uwe/23908e6a-c5ad-4032-b121-e9f5bf0b0d50.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/sMerfcGLjX7D5ZfxHHcNXk/8163cbf9-bbd9-4b6f-bf6f-d27cfa99388d.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/6gEEDUuzyuu7QfWTRiGjaw/e57a8eb9-f363-4862-a011-3ba84541bf54.webp'
}];
const heroImages = ['https://cdn.magicpatterns.com/uploads/7hAbtPEv66tS7cRaYNE4r9/me_gusta_mucho_202604131502.png', 'https://cdn.magicpatterns.com/uploads/pYiKeNHymWKoebcvZXR1Ds/Diseno_sin_titulo_(1).png'];
export function App() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [spots, setSpots] = useState(18);
  const [joinedCount, setJoinedCount] = useState(3265);
  const [viewers, setViewers] = useState(48);
  const [showSaleAnimations, setShowSaleAnimations] = useState(false);
  const [selectedTier, setSelectedTier] = useState(0);
  const [selectedSubKit, setSelectedSubKit] = useState(0);
  const [timeLeft, setTimeTimeLeft] = useState(887); // 14:47 in seconds
  const subKits = [{
    id: 0,
    name: 'Kit Reiki Terapéutico',
    icon: '🍃',
    link: 'https://codex-labs.shop/cart/43552268189755:1'
  }, {
    id: 1,
    name: 'Kit Constelaciones Familiar',
    icon: '🌸',
    link: 'https://codex-labs.shop/cart/43553639530555:1'
  }, {
    id: 2,
    name: 'Kit Tarot Terapéutico',
    icon: '🃏',
    link: 'https://codex-labs.shop/cart/43555165241403:1'
  }];
  const tiers = [{
    id: 0,
    name: 'Solo Kit Profesional Radiestesia',
    description: 'Guía Radiestesia Terapéutica PDF + 5 Bonos',
    price: '$19.990',
    originalPrice: '$59.900',
    link: 'https://codex-labs.shop/cart/43541449310267:1',
    badge: null
  }, {
    id: 1,
    name: 'Guía Radiestesia + 1 Kit Opcional',
    description: 'Elegí un kit adicional para complementar tu formación',
    price: '$29.990',
    originalPrice: '$79.900',
    link: subKits[selectedSubKit].link,
    badge: 'MÁS ELEGIDO'
  }, {
    id: 2,
    name: 'PORTAL TERAPÉUTICO COMPLETO ✨',
    description: 'Acceso total: Radiestesia + Reiki + Constelaciones + Tarot + Todos los bonos',
    price: '$39.990',
    originalPrice: '$99.900',
    link: 'https://codex-labs.shop/cart/43552261701691:1',
    badge: 'MEJOR VALOR 🔥'
  }];
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 3000);
    return () => clearInterval(viewerInterval);
  }, []);
  useEffect(() => {
    // Meta Pixel Integration
    const pixelId = '2048912638989861';
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    // @ts-ignore
    window.fbq('init', pixelId);
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const handleSale = () => {
    setSpots(prev => prev > 2 ? prev - 1 : prev);
    setJoinedCount(prev => prev + 1);
    setShowSaleAnimations(true);
    setTimeout(() => setShowSaleAnimations(false), 2000);
  };
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const currentDate = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const scrollToPurchase = () => {
    // @ts-ignore
    if (window.fbq) {
      // @ts-ignore
      window.fbq('track', 'InitiateCheckout');
    }
    document.getElementById('purchase')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="min-h-screen bg-[#FAF7FF] text-violet-950 font-sans selection:bg-violet-200 selection:text-violet-950" data-id="element-1" data-mp="2ko3lg3k6pa0x">
      <EnergyBackground data-id="element-2" data-mp="2tj9poy3t3k1c" />
      <LavenderBranches data-id="element-3" data-mp="2thgjtl10p3ue" />
      <UrgencyBanner spots={spots} onSale={handleSale} viewers={viewers} data-id="element-4" data-mp="2thyrrex1l6i5" />

      {/* Floating Energy Symbols Overlay — covers first 3 sections */}
      <div className="relative" data-id="element-5" data-mp="33gedf2davnk9">
        <FloatingEnergySymbols data-id="element-6" data-mp="33hfr7elpao5o" />

        {/* Hero Section */}
        <header className="relative overflow-hidden bg-[#0f051a]" data-id="element-7" data-mp="2yicwamzfpybf">
          <HeroAmbientEffects data-id="element-8" data-mp="2yg07bpv9enov" />
          <div className="relative" data-id="element-9" data-mp="3i33bczrwlsoj">
            <img src="https://cdn.magicpatterns.com/uploads/dsfdjiFviqE19DTtKXCqD2/ChatGPT_Image_6_ago_2026,_21_57_32.png" alt="Conviértete en Terapeuta de Radiestesia" className="w-full h-auto block" data-id="element-10" data-mp="3i466xl068jas" />

            {/* Premium CTA Button Overlay */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 w-full max-w-4xl px-4 text-center" data-id="element-11" data-mp="3d2p5nodryuto">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="flex flex-col items-center relative" style={{
              paddingTop: "0px",
              paddingBottom: "0px",
              width: "360px",
              height: "78px"
            }} data-id="element-12" data-mp="2zb2ajrvkjsch">
                {/* Premium Breathing Glow Behind CTA */}
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[120%] bg-gradient-to-r from-[#D4AF37]/20 via-[#A78BFA]/20 to-[#D4AF37]/20 blur-2xl rounded-full pointer-events-none" animate={{
                opacity: [0.3, 0.6, 0.3],
                scale: [0.9, 1.1, 0.9]
              }} transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-13" data-mp="2ge9h9vmeiri5" />

                <motion.button onClick={scrollToPurchase} animate={{
                y: [0, -8, 0],
                boxShadow: ['0 10px 25px -5px rgba(167, 139, 250, 0.3)', '0 20px 35px -5px rgba(212, 175, 55, 0.4)', '0 10px 25px -5px rgba(167, 139, 250, 0.3)']
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative group px-6 py-2 rounded-full bg-gradient-to-r from-[#D4AF37] via-[#F9E29C] to-[#A78BFA] text-violet-950 font-black text-sm md:text-base tracking-wider uppercase border border-white/40 overflow-hidden transition-transform active:scale-95 shadow-2xl mt-[0px] pt-[0px] pb-[0px] mb-[35px]" data-id="element-14" data-mp="2vug3u2hzkf5q">
                  {/* Glossy Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-black/5 pointer-events-none" data-id="element-15" data-mp="1y80ob694drkt" />

                  {/* Shine Effect */}
                  <motion.div animate={{
                  x: ['-100%', '200%']
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                  repeatDelay: 2
                }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -skew-x-12 pointer-events-none" data-id="element-16" data-mp="9po2p2a2f42s" />

                  <span className="relative z-10 flex items-center gap-3 text-[14px]" style={{
                  width: "294px",
                  height: "44px"
                }} data-id="element-17" data-mp="nnmd5wl9mgd0">
                    QUIERO APRENDER RADIESTESIA AHORA
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-18" data-mp="3r6fjppfmm4xo" />
                  </span>
                </motion.button>
              </motion.div>
            </div>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/6DRQnioTaCZj4PNqXUBVFD/ChatGPT_Image_6_ago_2026,_22_11_17.png" alt="Metodología de Radiestesia" className="w-full h-auto block" data-id="element-19" data-mp="4re38fhb5hbd" />
          <img src="https://cdn.magicpatterns.com/uploads/wqdZhAq4wXyg5o81dJeF4Y/ChatGPT_Image_6_ago_2026,_22_23_32.webp" alt="Kit Completo de Radiestesia" className="w-full h-auto block" data-id="element-20" data-mp="2f758uxzgc1d3" />
        </header>

        <WhatYouReceive data-id="element-21" data-mp="2p6man4bl1v3s" />

        {/* Intermediate CTA */}
        <section className="py-12 text-center relative z-10 bg-[#FFFBF2] pt-[10px] pb-[10px]" data-id="element-22" data-mp="2u44b06bjb206">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-container px-4" data-id="element-23" data-mp="1wgvuyquayl5k">
            <motion.button onClick={scrollToPurchase} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="relative group px-10 py-5 rounded-2xl bg-gradient-to-r from-[#D4AF37] via-[#F9E29C] to-[#A78BFA] text-violet-950 font-black text-lg md:text-xl tracking-wider uppercase shadow-xl hover:shadow-violet-200/50 transition-all overflow-hidden border border-white/40" data-id="element-24" data-mp="2b5s1gjgpnq42">
              {/* Shine Effect */}
              <motion.div animate={{
              x: ['-100%', '200%']
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear',
              repeatDelay: 2
            }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 pointer-events-none" data-id="element-25" data-mp="3n0x2ye5gyyrb" />
              <span className="relative z-10 flex items-center justify-center gap-3" data-id="element-26" data-mp="2za12ksmy6v8g">
                QUIERO MI KIT PROFESIONAL AHORA
                <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" data-id="element-27" data-mp="2u9n7eb6g3fru" />
              </span>
            </motion.button>
            <p className="mt-6 text-violet-500 font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-28" data-mp="2flv81bww7pte">
              <ShieldCheck size={16} data-id="element-29" data-mp="26mrltz2fiuao" />
              Acceso inmediato y seguro
            </p>
          </motion.div>
        </section>

        {/* Testimonials */}
        <section className="py-32 relative overflow-hidden bg-gradient-to-b from-[#F5F0FF] to-[#FDF6F8] pt-[5px] pb-[5px]" data-id="element-30" data-mp="2aova5oa75tdd">
          {/* Soft blur background elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#FDF6F8]/80 via-transparent to-transparent pointer-events-none" data-id="element-31" data-mp="33daffj3j0xbi" />
          <div className="absolute -left-40 top-40 w-96 h-96 bg-[#F5E1EA]/40 rounded-full blur-3xl pointer-events-none" data-id="element-32" data-mp="2tlwfja7nxvhu" />
          <div className="absolute -right-40 bottom-20 w-96 h-96 bg-[#FDF6F8] rounded-full blur-3xl shadow-[0_0_100px_rgba(212,175,55,0.08)] pointer-events-none" data-id="element-33" data-mp="3hap6es9fhd5l" />

          <div className="max-container px-4 relative z-10" data-id="element-34" data-mp="1anhu3md4ylwi">
            <div className="text-center mb-16 max-w-3xl mx-auto relative" data-id="element-35" data-mp="15pq1eq4o2vr9">
              <div className="absolute -top-8 left-0 md:-left-8 text-[#D4AF37] opacity-70" data-id="element-36" data-mp="15ndlsjmdsxhd">
                <Sparkles size={32} fill="currentColor" data-id="element-37" data-mp="1kgfexz5lomya" />
              </div>
              <span className="text-violet-600 font-black uppercase tracking-[0.3em] mb-6 block text-xs md:text-sm" data-id="element-38" data-mp="1fgkr64h4gdhg">
                Testimonios Reales
              </span>
              <h3 className="text-4xl md:text-7xl text-violet-950 tracking-tight font-medium font-['Playfair_Display'] mb-8 leading-[1.1]" data-id="element-39" data-mp="mrdrh25xlhum">
                <span className="text-violet-600 italic" data-id="element-40" data-mp="11p66zubha68t">Transforma</span> tus
                <br data-id="element-41" data-mp="wnyyft1ih313" />
                sesiones hoy
              </h3>
              <p className="text-violet-500 font-medium text-lg md:text-xl leading-relaxed max-w-2xl mx-auto" data-id="element-42" data-mp="2d2ibm7f2asw1">
                Mira cómo terapeutas como tú ya están logrando resultados
                rápidos y profundos con nuestras herramientas.
              </p>
            </div>

            <div className="max-w-4xl mx-auto relative" data-id="element-43" data-mp="mlwhgrijqygg">
              <div className="overflow-hidden" data-id="element-44" data-mp="mmg0ycnuhuwl">
                <motion.div key={currentTestimonial} drag="x" dragConstraints={{
                left: 0,
                right: 0
              }} onDragEnd={(_, info) => {
                if (info.offset.x > 100) {
                  prevTestimonial();
                } else if (info.offset.x < -100) {
                  nextTestimonial();
                }
              }} initial={{
                opacity: 0,
                x: 100
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -100
              }} transition={{
                duration: 0.5,
                ease: 'easeOut'
              }} className="bg-[#FFFBF2] p-4 md:p-8 rounded-[2rem] shadow-[0_30px_60px_-12px_rgba(167,139,250,0.18)] border border-[#EDE4FF] relative backdrop-blur-sm cursor-grab active:cursor-grabbing" data-id="element-45" data-mp="qnrnuafc4213">
                  <div className="flex flex-col items-center gap-6" data-id="element-46" data-mp="1kh8m0956quna">
                    <div className="flex justify-center text-[#D4AF37] gap-1" data-id="element-47" data-mp="2h4n4scybb3oh">
                      {[...Array(5)].map((_, idx) => <Star key={idx} size={24} fill="currentColor" strokeWidth={1} data-id="element-48" data-mp="2c8oh6rkt5pp4" data-mpi={idx} />)}
                    </div>

                    <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-[#EDE4FF] shadow-inner" data-id="element-49" data-mp="2zwzfbmz4db9p">
                      <img src={testimonials[currentTestimonial].image} alt="Testimonio" className="w-full h-auto object-contain" data-id="element-50" data-mp="2v46az07kc4y5" />
                    </div>

                    <div className="text-center" data-id="element-51" data-mp="jinmve1f0t47">
                      <span className="inline-block bg-[#F5E1EA] text-[#9D5A75] text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full" data-id="element-52" data-mp="jhuds6ezhriw">
                        Experiencia Real Verificada
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Navigation Arrows */}
              <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-[#FFFBF2] p-4 rounded-full shadow-xl text-violet-600 hover:bg-[#F5F0FF] transition-colors z-20 hidden md:block" data-id="element-53" data-mp="kjnvcf8sag4">
                <ArrowRight className="rotate-180" size={24} data-id="element-54" data-mp="1zv2oqpeqmhou" />
              </button>
              <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-[#FFFBF2] p-4 rounded-full shadow-xl text-violet-600 hover:bg-[#F5F0FF] transition-colors z-20 hidden md:block" data-id="element-55" data-mp="3kdmurwpzdys0">
                <ArrowRight size={24} data-id="element-56" data-mp="3pd7uwagw0d6b" />
              </button>

              {/* Pagination Dots */}
              <div className="flex justify-center gap-3 mt-12" data-id="element-57" data-mp="6yqjqrdapzh8">
                {testimonials.map((_, i) => <button key={i} onClick={() => setCurrentTestimonial(i)} className={`h-3 rounded-full transition-all duration-300 ${currentTestimonial === i ? 'w-12 bg-[#A78BFA]' : 'w-3 bg-[#EDE4FF] hover:bg-[#D6C6F5]'}`} data-id="element-58" data-mp="2x51eqhzsszn" data-mpi={i} />)}
              </div>

              {/* Sliding Instruction */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} className="flex flex-col items-center gap-2 mt-8 md:hidden" data-id="element-59" data-mp="2dqrh86j54w9m">
                <div className="flex items-center gap-4 text-violet-400" data-id="element-60" data-mp="21n3yatscna4q">
                  <motion.div animate={{
                  x: [-5, 5, -5]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-61" data-mp="26lyh3vqbgyou">
                    <ArrowRight className="rotate-180" size={16} data-id="element-62" data-mp="1ivb5azda5ox0" />
                  </motion.div>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em]" data-id="element-63" data-mp="1nvyudfhy7vol">
                    Desliza para ver más
                  </span>
                  <motion.div animate={{
                  x: [5, -5, 5]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-64" data-mp="2zacc02m3zew6">
                    <ArrowRight size={16} data-id="element-65" data-mp="dwnxvc99rvpo" />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Offer Valid Today Section */}
        <section className="py-12 bg-[#F5F0FF] relative z-10 pt-[0px]" data-id="element-66" data-mp="3z9t49wpkvd7">
          <div className="max-w-2xl mx-auto px-4 space-y-6" data-id="element-67" data-mp="3w32b18zps1n0">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-[#FCEFC7] rounded-3xl p-8 text-center border-2 border-[#F9E29C] shadow-lg" data-id="element-68" data-mp="3w1ivxlpsnwmt">
              <h3 className="text-violet-900 font-black text-xl md:text-2xl uppercase tracking-widest leading-tight" data-id="element-69" data-mp="1ke2npnh8nk28">
                OFERTA VÁLIDA SOLO POR HOY:
                <br data-id="element-70" data-mp="279app45vr0nd" />
                <span className="text-2xl md:text-3xl mt-2 block" data-id="element-71" data-mp="278rh4e24dom0">
                  {currentDate}
                </span>
              </h3>
            </motion.div>

            <div className="space-y-4" data-id="element-72" data-mp="1xj7z1am8dys7">
              {['Descargue todos los archivos en formato PDF, guárdelos en una carpeta segura y téngalos para siempre.', 'Pago único y descarga inmediata.', 'Materiales listos para impresión.', 'Herramientas desarrolladas por terapeutas especialistas.'].map((text, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-white rounded-2xl p-6 flex items-center gap-4 shadow-sm border border-violet-100 group hover:shadow-md transition-shadow" data-id="element-73" data-mp="1ekdka1mroyc1" data-mpi={index}>
                  <div className="shrink-0 w-8 h-8 rounded-full border-2 border-violet-400 flex items-center justify-center text-violet-600 group-hover:bg-violet-50 transition-colors" data-id="element-74" data-mp="3v5il7w4tzoe9">
                    <CheckCircle2 size={20} data-id="element-75" data-mp="32iedhjecsvys" />
                  </div>
                  <p className="text-violet-900 font-bold text-sm md:text-base leading-relaxed" data-id="element-76" data-mp="37gpnbu4nf6h0">
                    {text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section className="py-24 bg-[#F5F0FF] pt-[40px] pb-[40px] relative overflow-hidden group/bonuses" data-id="element-77" data-mp="msb8r6y6maao">
          <PremiumBonusEffect data-id="element-78" data-mp="rr4qxcoeyph6" />
          <GiftBackground data-id="element-79" data-mp="20mrvxkvcqo8" />

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-yellow-200/10 via-transparent to-transparent opacity-0 group-hover/bonuses:opacity-100 transition-opacity duration-1000 pointer-events-none" data-id="element-80" data-mp="1zuvwlnd0qxf" />

          <div className="max-container px-4 relative z-10" data-id="element-81" data-mp="3ai0uddng8crj">
            <div className="text-center mb-16 relative" data-id="element-82" data-mp="3p5vp0zl0or79">
              <TitleSparkles data-id="element-83" data-mp="3p7f4wp2rh08i" />
              <motion.div animate={{
              rotate: [-8, 8, -8],
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="inline-block mb-6 text-violet-600" data-id="element-84" data-mp="3p7oy0lj3rnif">
                <Gift size={80} strokeWidth={1.5} data-id="element-85" data-mp="1774kff5wlmc4" />
              </motion.div>

              <h3 className="md:text-7xl text-violet-950 tracking-tight text-[42px] font-['Playfair_Display'] mb-6 font-[500] leading-tight" data-id="element-86" data-mp="1bzxoe0tjsqn5">
                ¡Regalos{' '}
                <span className="text-violet-600 italic" data-id="element-87" data-mp="128kwxabnw2hn">Exclusivos!</span>
              </h3>

              <motion.div initial={{
              scale: 0.9,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} viewport={{
              once: true
            }} className="inline-flex items-center gap-3 px-8 py-3 rounded-full bg-violet-600 text-white text-sm md:text-lg font-black uppercase tracking-[0.25em] shadow-2xl shadow-violet-300/50" data-id="element-88" data-mp="1pyfaju3t1v2y">
                <Sparkles size={20} className="animate-pulse text-yellow-300" data-id="element-89" data-mp="9lgpyzt0de38" />
                <span data-id="element-90" data-mp="ps3fticckybx">100% GRATIS SOLO POR HOY</span>
                <Sparkles size={20} className="animate-pulse text-yellow-300" data-id="element-91" data-mp="ptmuj4icv1bl" />
              </motion.div>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-92" data-mp="g1qrdf1ewl0i">
              <BonusCard number="01" title="Gráficos Profesionales de Radiestesia" image="https://cdn.magicpatterns.com/uploads/fWH95PnHKNkWMsTd7g9mXi/b496c878-fd89-40a9-b8f2-fd99fe7aa423.webp" description="Utilizá gráficos radiestésicos listos para sesiones, medición energética y armonización." value="$47 USD" data-id="element-93" data-mp="g2a0c6933z2e" />
              <BonusCard number="02" title="Planilla Terapéutica de Seguimiento" image="https://cdn.magicpatterns.com/uploads/bxUEDFrzJ5EkaNRh9BsxcQ/5a6606fc-c970-40fd-b474-499753f7df0f.webp" description="Organizá consultas, evolución energética y observaciones de cada cliente." value="$37 USD" data-id="element-94" data-mp="yukyxnwphaup" />
              <BonusCard number="03" title="Cuaderno de Prácticas Radiestésicas" image="https://cdn.magicpatterns.com/uploads/q7ufTu9t8SmcRX8BNXgKhj/3a00b4de-e208-4b45-8d3a-9d9315265ed6.webp" description="Ejercicios guiados para desarrollar precisión, intuición y confianza con el péndulo." value="$27 USD" data-id="element-95" data-mp="1v0xpxbz8a2cc" />
              <BonusCard number="04" title="+100 Preguntas Terapéuticas" image="https://cdn.magicpatterns.com/uploads/xiGaUkJeBd81m7UWremYi7/52b42563-aec8-4d14-9170-a3f9724517d5.webp" description="Preguntas diseñadas para profundizar sesiones y detectar bloqueos energéticos." value="$37 USD" data-id="element-96" data-mp="2drozoen4nur9" />
              <BonusCard number="05" title="Guía Profesional del Uso de Péndulo" image="https://cdn.magicpatterns.com/uploads/j2ZW3GthfasKqWVYgd1ECf/0aac60a9-181b-4eff-aa68-cf837eafcf2d.webp" description="Aprendé técnicas, movimientos, limpieza y uso correcto del péndulo paso a paso." value="$27 USD" data-id="element-97" data-mp="se2ogzs0kyxu" />
            </div>
          </div>
        </section>
      </div>

      {/* Purchase Section */}
      <section id="purchase" className="py-24 bg-[#FAF4FF] relative overflow-hidden pt-[30px] pb-[30px]" data-id="element-98" data-mp="24madcqy4b21o">
        <div className="max-w-4xl mx-auto px-4 relative z-10 space-y-[15px]" data-id="element-99" data-mp="2jirobh2eaenp">
          <div className="text-center space-y-4 mb-12" data-id="element-100" data-mp="2elrh32tt75v0">
            <div className="flex flex-col items-center gap-2" data-id="element-101" data-mp="2sdo44sgmepkj">
              <p className="text-violet-400 text-xs font-black uppercase tracking-[0.2em]" data-id="element-102" data-mp="2sf92kt622sno">
                + {joinedCount} ALUMNOS
              </p>
              <div className="flex text-yellow-400 gap-1" data-id="element-103" data-mp="32cnce9xh4j70">
                {[...Array(5)].map((_, i) => <Star key={i} size={20} fill="currentColor" data-id="element-104" data-mp="2xdu3kut4tgd6" data-mpi={i} />)}
              </div>
              <span className="text-violet-300 text-sm font-bold" data-id="element-105" data-mp="3gyu8mv6nbhvz">
                (1.728 reseñas)
              </span>
            </div>

            <div className="inline-block px-4 py-1 rounded-full bg-[#FFFBF2] border border-[#F9E29C] text-[#D4AF37] text-[10px] font-black uppercase tracking-widest" data-id="element-106" data-mp="37050vv8grdtg">
              ECOSISTEMA TERAPÉUTICO PROFESIONAL
            </div>

            <h2 className="text-4xl md:text-6xl font-medium font-['Playfair_Display'] text-violet-950 leading-tight" data-id="element-107" data-mp="2y7jhqe0x7543">
              Elegí el camino terapéutico <br data-id="element-108" data-mp="330mhx507dct9" />
              <span className="text-violet-600 italic" data-id="element-109" data-mp="2o9ncu3ykt6ef">
                que mejor conecte con vos
              </span>
            </h2>

            <p className="text-violet-400 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-110" data-mp="2t9ru3w7b655p">
              Accedé de forma inmediata a nuestros programas terapéuticos
              profesionales en{' '}
              <span className="font-bold text-violet-600" data-id="element-111" data-mp="2j991abxrnebn">
                formato digital PDF
              </span>{' '}
              con acceso de por vida, material descargable y futuras
              actualizaciones incluidas.
            </p>
          </div>

          <div className="space-y-4 mb-12" data-id="element-112" data-mp="2prlxrpm6j4ki">
            <p className="text-center text-[10px] font-black text-violet-300 uppercase tracking-[0.3em] mb-6" data-id="element-113" data-mp="2pqastmgzzqrt">
              ↓ SELECCIONÁ EL PACK QUE PREFIERAS: ↓
            </p>

            <div className="grid gap-4" data-id="element-114" data-mp="2gs8wvlc4fobe">
              {tiers.map(tier => <div key={tier.id} className="space-y-0" data-id="element-115" data-mp="2bzxmfojr8k4l">
                  <button onClick={() => setSelectedTier(tier.id)} className={`relative w-full text-left p-5 md:p-6 border-2 transition-all duration-300 ${selectedTier === tier.id ? tier.id === 1 ? 'bg-white border-violet-600 rounded-t-2xl z-10' : 'bg-white border-violet-600 rounded-2xl shadow-xl z-10' : 'bg-white/40 border-[#EDE4FF] rounded-2xl hover:border-violet-200 opacity-90'}`} data-id="element-116" data-mp="2bye3tramlwzn">
                    {tier.badge && <div className={`absolute -top-3 right-6 px-3 py-1 rounded-full text-[9px] font-black text-white shadow-sm ${tier.id === 2 ? 'bg-gradient-to-r from-violet-600 to-indigo-600' : 'bg-[#FFA500]'}`} data-id="element-117" data-mp="8jasy6vm3e5q">
                        {tier.badge}
                      </div>}

                    <div className="flex items-center justify-between gap-4" data-id="element-118" data-mp="3l1hd94l4qdgx">
                      <div className="flex items-center gap-4" data-id="element-119" data-mp="3q024ieielmt8">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedTier === tier.id ? 'border-violet-600 bg-white' : 'border-violet-200'}`} data-id="element-120" data-mp="3q0bva4b9vxzz">
                          {selectedTier === tier.id && <div className="w-3 h-3 rounded-full bg-violet-600" data-id="element-121" data-mp="328dgvbgj1mim" />}
                        </div>
                        <div data-id="element-122" data-mp="2gz5f375c69t3">
                          <h4 className={`font-black text-sm md:text-lg transition-colors ${selectedTier === tier.id ? 'text-violet-950' : 'text-violet-950/70'}`} data-id="element-123" data-mp="2h0ykkj47qnzi">
                            {tier.name}
                          </h4>
                          <p className="text-violet-400 text-[10px] md:text-xs font-medium" data-id="element-124" data-mp="2bziwsjani3df">
                            {tier.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-125" data-mp="23172a66nylfp">
                        <p className="text-violet-200 text-[10px] md:text-xs line-through font-bold" data-id="element-126" data-mp="1obhtppw8rf5e">
                          {tier.originalPrice}
                        </p>
                        <p className={`text-lg md:text-2xl font-black transition-colors ${selectedTier === tier.id ? 'text-[#319E0D]' : 'text-violet-950'}`} data-id="element-127" data-mp="1fabah927jq8g">
                          {tier.price}
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Sub-options for Tier 1 */}
                  <AnimatePresence data-id="element-128" data-mp="19o12wpffdqej">
                    {tier.id === 1 && selectedTier === 1 && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-129" data-mp="15mxng41ahwdu">
                        <div className="bg-white border-2 border-t-0 border-violet-600 rounded-b-2xl p-5 md:p-6 pt-4 space-y-4" data-id="element-130" data-mp="22c6kx4f3h1ao">
                          <p className="text-[10px] font-black text-violet-600 uppercase tracking-widest mb-1" data-id="element-131" data-mp="1xfg6elixudrd">
                            SELECCIONÁ TU KIT ADICIONAL:
                          </p>
                          <div className="grid gap-3" data-id="element-132" data-mp="10023mymm3p16">
                            {subKits.map(sub => <button key={sub.id} onClick={() => setSelectedSubKit(sub.id)} className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all duration-200 ${selectedSubKit === sub.id ? 'bg-white border-violet-600 shadow-sm' : 'bg-white border-[#EDE4FF] hover:border-violet-200'}`} data-id="element-133" data-mp="13z12c1hr2pk6">
                                <div className="flex items-center gap-3" data-id="element-134" data-mp="1rr82lbqljgov">
                                  <span className="text-xl" data-id="element-135" data-mp="3pghkqg0g2xkc">{sub.icon}</span>
                                  <span className={`font-bold text-sm md:text-base ${selectedSubKit === sub.id ? 'text-violet-950' : 'text-violet-400'}`} data-id="element-136" data-mp="3pikk3qnhm31q">
                                    {sub.name}
                                  </span>
                                </div>
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selectedSubKit === sub.id ? 'border-violet-600' : 'border-violet-100'}`} data-id="element-137" data-mp="3c7z8lmlqfbt0">
                                  {selectedSubKit === sub.id && <div className="w-2.5 h-2.5 rounded-full bg-violet-600" data-id="element-138" data-mp="3tqjioif535l" />}
                                </div>
                              </button>)}
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </div>

          <div className="max-w-2xl mx-auto" data-id="element-139" data-mp="79a2getz6u0n">
            <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-violet-100" data-id="element-140" data-mp="78h15tdscwpw">
              <div className="p-0" data-id="element-141" data-mp="c2la2i6m3etm">
                <div className="relative p-4 md:p-8" data-id="element-142" data-mp="3uf193f5sr59j">
                  <AnimatePresence mode="wait" data-id="element-143" data-mp="3ugc9d5019uvw">
                    <motion.img key={`${selectedTier}-${selectedSubKit}`} initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 1.05
                  }} transition={{
                    duration: 0.4,
                    ease: 'easeOut'
                  }} src={selectedTier === 2 ? 'https://cdn.magicpatterns.com/uploads/pbQXfKrVPvf48XpzmBwa41/ChatGPT_Image_27_may_2026,_15_23_37.webp' : selectedTier === 1 && selectedSubKit === 0 ? 'https://cdn.magicpatterns.com/uploads/2r9Mj4iBW7L6A8todMzw9A/2b00f44c-c4f2-49cd-a351-131ac0bd849f.webp' : selectedTier === 1 && selectedSubKit === 1 ? 'https://cdn.magicpatterns.com/uploads/kEdr2PohEnmrNzmDrDbHft/05393dcc-e8a9-4158-8638-bcd33da82c23.webp' : selectedTier === 1 && selectedSubKit === 2 ? 'https://cdn.magicpatterns.com/uploads/b9SYxr2rD34ETsKdwsjuB5/ChatGPT_Image_29_may_2026,_10_34_17.webp' : 'https://cdn.magicpatterns.com/uploads/aSTNCx5fwV4qfBoHpoAzzg/1b00af68-f628-437e-909d-dc301a18f692.webp'} alt={tiers[selectedTier].name} className="w-full h-auto rounded-3xl shadow-sm" data-id="element-144" data-mp="2a71b5h497xy" />
                  </AnimatePresence>
                </div>

                <div className="p-8 md:p-12 text-center pt-0 space-y-8" data-id="element-145" data-mp="mhm827rldfx">
                  <div className="space-y-6" data-id="element-146" data-mp="5dzd1xg2pxn6">
                    <div className="bg-[#F5F0FF] rounded-3xl p-6 md:p-8 border border-violet-100 pt-[0px] pb-[0px]" data-id="element-147" data-mp="5fap0kjoabpd">
                      <div className="inline-flex gap-2 px-4 py-1 bg-violet-600 text-white rounded-full font-black uppercase tracking-widest shadow-md text-[10px] mb-[0px] items-center justify-start" data-id="element-148" data-mp="9hxbs71hl6fp">
                        <Sparkles size={14} className="text-yellow-300" data-id="element-149" data-mp="3dw5gz4yxmboo" />
                        66% OFF HOY
                      </div>
                      <p className="text-violet-950 font-medium text-lg md:text-xl" data-id="element-150" data-mp="3mv91mddqyz5a">
                        3 cuotas sin interés de{' '}
                        <span className="text-[#319E0D] font-black text-2xl md:text-3xl" data-id="element-151" data-mp="x5f8mgzlyupj">
                          $
                          {(parseInt(tiers[selectedTier].price.replace(/[^0-9]/g, '')) / 3).toLocaleString('es-AR', {
                          maximumFractionDigits: 0
                        })}
                        </span>
                      </p>
                    </div>

                    <div className="relative group" data-id="element-152" data-mp="yfh7928zyr7l">
                      <div className="absolute -inset-2 bg-[#82C91E]/20 rounded-[2rem] blur-xl opacity-0 group-hover:opacity-100 transition duration-500" data-id="element-153" data-mp="tj8pdh3f0jwq"></div>

                      <motion.a href={tiers[selectedTier].link} target="_blank" rel="noopener noreferrer" animate={{
                      scale: [1, 1.02, 1],
                      boxShadow: ['0 15px 30px rgba(130,201,30,0.4)', '0 20px 40px rgba(130,201,30,0.6)', '0 15px 30px rgba(130,201,30,0.4)']
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} className="relative w-full bg-[#82C91E] text-white py-6 md:py-8 rounded-3xl font-black flex items-center justify-center gap-3 hover:bg-[#74b816] transition-all hover:scale-[1.05] active:scale-95 uppercase tracking-tight overflow-hidden text-2xl md:text-4xl" data-id="element-154" data-mp="jjriwxgca1zp">
                        {/* Shine Effect */}
                        <motion.div animate={{
                        x: ['-100%', '200%']
                      }} transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: 'linear',
                        repeatDelay: 1
                      }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 pointer-events-none" data-id="element-155" data-mp="25fmjvifrtjuv" />
                        <motion.span animate={{
                        rotate: [-5, 5, -5]
                      }} transition={{
                        duration: 2,
                        repeat: Infinity
                      }} data-id="element-156" data-mp="mlt4djyt4lrz">
                          <Sparkles className="text-yellow-300 w-6 h-6 md:w-10 md:h-10" data-id="element-157" data-mp="15j42v2cm7b6d" />
                        </motion.span>
                        ACCEDER AHORA
                        <motion.span animate={{
                        rotate: [5, -5, 5]
                      }} transition={{
                        duration: 2,
                        repeat: Infinity
                      }} data-id="element-158" data-mp="11ik825fikjne">
                          <Sparkles className="text-yellow-300 w-6 h-6 md:w-10 md:h-10" data-id="element-159" data-mp="1djd77i9nhhud" />
                        </motion.span>
                      </motion.a>
                    </div>

                    <div className="space-y-4" data-id="element-160" data-mp="1nhaspy04l0ja">
                      <motion.div animate={{
                      scale: [1, 1.05, 1]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} className="flex items-center justify-center gap-2 text-violet-400 font-bold" data-id="element-161" data-mp="1nghujlf8fjcr">
                        <span className="text-xl" data-id="element-162" data-mp="2k7ac6nhtdjgn">🔥</span>
                        <span data-id="element-163" data-mp="2k7tkdchnwthh">¡Últimos {spots} cupos en oferta!</span>
                      </motion.div>

                      <div className="flex items-center justify-center gap-8 text-[10px] md:text-xs font-black uppercase tracking-widest text-violet-300 border-t border-violet-50 pt-4" data-id="element-164" data-mp="2faa2m2lr9yks">
                        <div className="flex items-center gap-2" data-id="element-165" data-mp="33stsjlqh3b4i">
                          <Zap size={14} className="text-yellow-400" data-id="element-166" data-mp="2puc57p4hc0n4" />
                          LA OFERTA EXPIRA EN:
                        </div>
                        <span className="text-violet-600 text-lg font-mono" data-id="element-167" data-mp="2tx8vm209h11m">
                          {formatTime(timeLeft)}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full h-1.5 bg-violet-50 rounded-full overflow-hidden" data-id="element-168" data-mp="265v1prmgoxej">
                        <motion.div initial={{
                        width: '100%'
                      }} animate={{
                        width: `${timeLeft / 887 * 100}%`
                      }} className="h-full bg-gradient-to-r from-violet-400 to-fuchsia-400" data-id="element-169" data-mp="2b4o7syzf5m4o" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust Section */}
                <div className="p-8 md:p-12 bg-violet-50/50 border-t-2 border-dashed border-violet-100 space-y-8 pt-[0px] pb-[0px] mb-[5px]" data-id="element-170" data-mp="3u4dyys6ycdjr">
                  <div className="text-center space-y-[0px]" data-id="element-171" data-mp="3pzo3v5k4b2zw">
                    <img src="https://cdn.magicpatterns.com/uploads/skccFFLzCHBogMzzjkfEmF/7ef4071d-c511-4325-9d9d-f2158f7b8632.jpg" alt="MercadoLíder Platinum" className="h-32 md:h-48 mx-auto object-contain shadow-sm rounded-2xl" data-id="element-172" data-mp="qlklefe6bopl" />
                    <img src="https://cdn.magicpatterns.com/uploads/eU118ieSDgWuz64T6oQtAx/capture_260410_163646.png" alt="Pago Seguro Garantizado" className="w-full max-w-[400px] mx-auto object-contain opacity-90" data-id="element-173" data-mp="cmcpbvdb4wpm" />
                  </div>

                  <div className="flex flex-row items-center justify-center md:gap-16 border-t border-violet-100 pt-8 mt-[0px] gap-[0px]" data-id="element-174" data-mp="1ct7z6nzqvjxb">
                    <img src="https://cdn.magicpatterns.com/uploads/7eCtKk7WA3cLyGaeie1cdM/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra Segura" className="h-24 md:h-32 object-contain transition-all" data-id="element-175" data-mp="1lvh5f1nzcr4w" />
                    <img src="https://cdn.magicpatterns.com/uploads/fpbSfdYZaTGuGJTHBKUizP/Selo_de_Garantia_de_7_Dias_PNG_Transparente_Sem_Fundo.png" alt="Garantía de 7 Días" className="h-28 md:h-36 object-contain transition-all" data-id="element-176" data-mp="20mgfkh4grnql" />
                  </div>
                </div>

                <div className="border-t border-violet-100" data-id="element-177" data-mp="f829xurhp08h">
                  <img src="https://cdn.magicpatterns.com/uploads/v8j4Lt9vmm43yrw1WX4iKu/c0009f87-e89c-4e3d-8468-fdb8d358afac.png" alt="Regalos Exclusivos Solo Por Hoy" className="w-full h-auto" data-id="element-178" data-mp="3g1yux8p537fb" />
                </div>
              </div>
            </div>
          </div>
          <LifetimeAccessBonus data-id="element-179" data-mp="3l2e73wxoq8vp" />
        </div>
      </section>

      {/* Creator Section */}
      <section className="bg-[#FFFBF2]" data-id="element-180" data-mp="2xcju9fdd8kbg">
        <img src="https://cdn.magicpatterns.com/uploads/h7PovxhuGKKpp4tgHZ9DVY/hf_20260522_220052_2bf523b5-44a1-4bec-bb01-c674a879982c.webp" alt="Sofía Alondra - Creadora del Kit Radiestesia Terapéutica" className="w-full h-auto block" data-id="element-181" data-mp="2scxkjfwqxm7e" />
      </section>

      <Reviews data-id="element-182" data-mp="329ad2agk1u0k" />

      <FAQ data-id="element-183" data-mp="2el9450fgsbkm" />

      <footer className="py-12 border-t border-[#EDE4FF] text-center text-violet-600 text-sm" data-id="element-184" data-mp="2ekr37qhux96d">
        <p data-id="element-185" data-mp="29n7oyql5swef">© 2025 Portal Terapéutico. Todos los derechos reservados.</p>
      </footer>
    </div>;
}